/* eslint-disable max-lines */
/**
 * Relationship List Visualization
 * Table/list view for detailed examination and bulk operations
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Checkbox,
  IconButton,
  Button,
  ButtonGroup,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Collapse,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import useTokenExaminerStore from '../stores/tokenExaminerStore';
import {
  getFaanaHealthColor,
  formatDimensionName,
  getDimensionColor,
  getGrowthIndicator,
} from '../utils/faanaScoring';
import RelationshipDetailPanel from '../components/RelationshipDetailPanel';

const FILTER_OPTIONS = [
  { key: 'all', label: 'All Relationships' },
  { key: 'confirmed', label: 'Confirmed Only' },
  { key: 'unconfirmed', label: 'Unconfirmed Only' },
  { key: 'critical', label: 'Critical Health' },
  { key: 'high_confidence', label: 'High Confidence' },
];

// eslint-disable-next-line max-lines-per-function
function RelationshipList() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('overallHealth');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [filterType, setFilterType] = useState('all');

  const {
    relationships,
    _entities,
    _selectedEntity,
    confirmRelationship,
    rejectRelationship,
    _updateRelationship,
    getFilteredRelationships,
  } = useTokenExaminerStore();

  // Filter and sort relationships
  const filteredAndSortedRelationships = useMemo(() => {
    let filtered = getFilteredRelationships();

    // Apply additional filters
    if (filterType === 'confirmed') {
      filtered = filtered.filter(rel => rel.isConfirmed);
    } else if (filterType === 'unconfirmed') {
      filtered = filtered.filter(rel => !rel.isConfirmed);
    } else if (filterType === 'critical') {
      filtered = filtered.filter(rel => (rel.overallHealth || 0) < 40);
    } else if (filterType === 'high_confidence') {
      filtered = filtered.filter(rel => rel.confidence === 'HIGH');
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(rel =>
        rel.relationshipType.toLowerCase().includes(term) ||
        rel.sourceEntity?.name?.toLowerCase().includes(term) ||
        rel.targetEntity?.name?.toLowerCase().includes(term) ||
        rel.evidence?.some(e => e.description.toLowerCase().includes(term))
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle special sorting cases
      if (sortBy === 'confidence') {
        const confidenceOrder = { 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
        aVal = confidenceOrder[aVal] || 0;
        bVal = confidenceOrder[bVal] || 0;
      } else if (sortBy === 'discoveredAt' || sortBy === 'confirmedAt') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return filtered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationships, searchTerm, sortBy, sortDirection, filterType, getFilteredRelationships]);

  // Pagination
  const paginatedRelationships = filteredAndSortedRelationships.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(new Set(paginatedRelationships.map(rel => rel.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (relationshipId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(relationshipId)) {
      newSelected.delete(relationshipId);
    } else {
      newSelected.add(relationshipId);
    }
    setSelectedRows(newSelected);
  };

  const handleExpandRow = (relationshipId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(relationshipId)) {
      newExpanded.delete(relationshipId);
    } else {
      newExpanded.add(relationshipId);
    }
    setExpandedRows(newExpanded);
  };

  const handleContextMenu = (event, relationship) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      relationship,
    });
  };

  const handleBatchConfirm = async () => {
    const relationshipsToConfirm = Array.from(selectedRows)
      .map(id => filteredAndSortedRelationships.find(rel => rel.id === id))
      .filter(rel => rel && !rel.isConfirmed);

    for (const rel of relationshipsToConfirm) {
      await confirmRelationship(rel.id, 'batch-operation');
    }

    setSelectedRows(new Set());
  };

  const handleBatchReject = async () => {
    const relationshipsToReject = Array.from(selectedRows)
      .map(id => filteredAndSortedRelationships.find(rel => rel.id === id))
      .filter(Boolean);

    for (const rel of relationshipsToReject) {
      await rejectRelationship(rel.id);
    }

    setSelectedRows(new Set());
  };

  // eslint-disable-next-line max-lines-per-function
  const renderRelationshipRow = (relationship) => {
    const isExpanded = expandedRows.has(relationship.id);
    const isSelected = selectedRows.has(relationship.id);
    const growthIndicator = getGrowthIndicator(relationship.growthIndicator || 'steady');

    return (
      <React.Fragment key={relationship.id}>
        <TableRow
          hover
          selected={isSelected}
          onContextMenu={(e) => handleContextMenu(e, relationship)}
        >
          <TableCell padding="checkbox">
            <Checkbox
              checked={isSelected}
              onChange={() => handleSelectRow(relationship.id)}
            />
          </TableCell>

          <TableCell>
            <IconButton
              size="small"
              onClick={() => handleExpandRow(relationship.id)}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </TableCell>

          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                {relationship.relationshipType}
              </Typography>
              {!relationship.isConfirmed && (
                <Chip
                  label="Unconfirmed"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          </TableCell>

          <TableCell>
            <Typography variant="body2" noWrap>
              {relationship.sourceEntity?.name || 'Unknown'} → {relationship.targetEntity?.name || 'Unknown'}
            </Typography>
          </TableCell>

          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 8,
                  backgroundColor: getFaanaHealthColor(relationship.overallHealth || 0),
                  borderRadius: 4,
                }}
              />
              <Typography variant="body2" fontWeight="bold">
                {relationship.overallHealth || 0}
              </Typography>
            </Box>
          </TableCell>

          <TableCell>
            <Chip
              label={relationship.confidence}
              size="small"
              color={
                relationship.confidence === 'HIGH' ? 'success' :
                relationship.confidence === 'MEDIUM' ? 'primary' : 'warning'
              }
              variant="outlined"
            />
          </TableCell>

          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {growthIndicator.icon === '📈' && <TrendingUpIcon sx={{ color: '#4CAF50' }} />}
              {growthIndicator.icon === '📉' && <TrendingDownIcon sx={{ color: '#F44336' }} />}
              {growthIndicator.icon === '➡️' && <TrendingFlatIcon sx={{ color: '#FF9800' }} />}
              <Typography variant="caption">
                {relationship.trajectoryPrediction || 'Unknown'}
              </Typography>
            </Box>
          </TableCell>

          <TableCell>
            <Typography variant="body2" color="text.secondary">
              {relationship.evidence?.length || 0} items
            </Typography>
          </TableCell>

          <TableCell>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setSelectedRelationship(relationship)}
              >
                <ViewIcon />
              </IconButton>
              {!relationship.isConfirmed && (
                <>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => confirmRelationship(relationship.id, 'table-action')}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => rejectRelationship(relationship.id)}
                  >
                    <CloseIcon />
                  </IconButton>
                </>
              )}
            </Box>
          </TableCell>
        </TableRow>

        {/* Expanded Row Content */}
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Card elevation={0} sx={{ backgroundColor: 'grey.50' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Relationship Dimensions
                    </Typography>

                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                      {Object.entries(relationship.dimensions || {}).map(([dimension, data]) => (
                        <Box key={dimension} sx={{ minWidth: 120 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDimensionName(dimension)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(data.score / 10) * 100}
                              sx={{
                                width: 60,
                                height: 6,
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: getDimensionColor(data.score),
                                },
                              }}
                            />
                            <Typography variant="body2" fontWeight="bold">
                              {data.score}/10
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>

                    {/* Evidence Summary */}
                    {relationship.evidence && relationship.evidence.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Evidence ({relationship.evidence.length} items)
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {relationship.evidence.slice(0, 3).map((evidence, idx) => (
                            <Box key={idx}>
                              <Typography variant="body2">
                                <strong>{evidence.type}:</strong> {evidence.description}
                              </Typography>
                            </Box>
                          ))}
                          {relationship.evidence.length > 3 && (
                            <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                              View {relationship.evidence.length - 3} more evidence items...
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Actionable Insights */}
                    {relationship.insights?.recommendations && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Recommended Actions
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {relationship.insights.recommendations.slice(0, 2).map((rec, idx) => (
                            <Chip
                              key={idx}
                              label={rec.action}
                              size="small"
                              variant="outlined"
                              color={
                                rec.priority === 'high' ? 'error' :
                                rec.priority === 'medium' ? 'warning' : 'success'
                              }
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const selectedCount = selectedRows.size;
  const confirmedCount = Array.from(selectedRows)
    .map(id => filteredAndSortedRelationships.find(rel => rel.id === id))
    .filter(rel => rel?.isConfirmed).length;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search relationships..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Filter"
            >
              {FILTER_OPTIONS.map(option => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant="body2" color="text.secondary">
            {filteredAndSortedRelationships.length} relationships
          </Typography>
        </Box>

        {/* Batch Actions */}
        {selectedCount > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">
                {selectedCount} relationships selected ({confirmedCount} already confirmed)
              </Typography>
              <ButtonGroup size="small">
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon />}
                  onClick={handleBatchConfirm}
                  disabled={selectedCount === confirmedCount}
                >
                  Confirm ({selectedCount - confirmedCount})
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleBatchReject}
                >
                  Reject
                </Button>
              </ButtonGroup>
            </Box>
          </Alert>
        )}
      </Paper>

      {/* Table */}
      <Paper sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <TableContainer sx={{ height: '100%' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedRows.size === paginatedRelationships.length && paginatedRelationships.length > 0}
                    indeterminate={selectedRows.size > 0 && selectedRows.size < paginatedRelationships.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell width={50} />
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'relationshipType'}
                    direction={sortBy === 'relationshipType' ? sortDirection : 'asc'}
                    onClick={() => handleSort('relationshipType')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>Entities</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'overallHealth'}
                    direction={sortBy === 'overallHealth' ? sortDirection : 'asc'}
                    onClick={() => handleSort('overallHealth')}
                  >
                    Health
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'confidence'}
                    direction={sortBy === 'confidence' ? sortDirection : 'asc'}
                    onClick={() => handleSort('confidence')}
                  >
                    Confidence
                  </TableSortLabel>
                </TableCell>
                <TableCell>Trajectory</TableCell>
                <TableCell>Evidence</TableCell>
                <TableCell width={120}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRelationships.map(renderRelationshipRow)}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredAndSortedRelationships.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Context Menu */}
      {contextMenu && (
        <Menu
          open={Boolean(contextMenu)}
          onClose={() => setContextMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          MenuListProps={{
            disablePortal: true,
          }}
        >
        {contextMenu && !contextMenu.relationship.isConfirmed && (
          <MenuItem
            onClick={() => {
              confirmRelationship(contextMenu.relationship.id, 'context-menu');
              setContextMenu(null);
            }}
          >
            Confirm Relationship
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setSelectedRelationship(contextMenu.relationship);
            setContextMenu(null);
          }}
        >
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            rejectRelationship(contextMenu.relationship.id);
            setContextMenu(null);
          }}
        >
          Reject Relationship
        </MenuItem>
        </Menu>
      )}

      {/* Relationship Detail Panel */}
      {selectedRelationship && (
        <RelationshipDetailPanel
          relationship={selectedRelationship}
          onClose={() => setSelectedRelationship(null)}
          onConfirm={() => {
            confirmRelationship(selectedRelationship.id, 'detail-panel');
            setSelectedRelationship(null);
          }}
          onReject={() => {
            rejectRelationship(selectedRelationship.id);
            setSelectedRelationship(null);
          }}
        />
      )}
    </Box>
  );
}

export default RelationshipList;